import { useSnackbar, WBTypography } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { Template } from '@admiin-com/ds-graphql';
import {
  useTemplateMutations,
  TemplateFormData,
} from '../../../hooks/useTemplateMutations/useTemplateMutations';
import SimpleDrawDlg, {
  SimpleDrawDlgProps,
} from '../../../components/SimpleDrawDlg/SimpleDrawDlg';
import DocumentCreateForm from './DocumentCreateForm';
import { BillCreateFormData } from '../../providers/TaskCreationProvider/TaskCreationProvider';

export interface DocumentCreateModalProps
  extends Omit<SimpleDrawDlgProps, 'children' | 'handleClose'> {
  open: boolean;
  onClose: () => void;
  template?: Template | null;
  onSuccess?: (template: Template) => void;
  taxCreate?: boolean;
  taskCreationContext?: BillCreateFormData;
}

export function DocumentCreateModal({
  onSuccess,
  taxCreate,
  taskCreationContext,
  ...props
}: DocumentCreateModalProps) {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();

  const { createTemplate, updateTemplate, creating, updating } =
    useTemplateMutations();

  const handleCreateTemplate = async (data: TemplateFormData) => {
    const newTemplate = await createTemplate(data);
    props.onClose();
    if (onSuccess) onSuccess(newTemplate);
  };

  const handleUpdateTemplate = async (data: TemplateFormData) => {
    if (!props.template?.id) return;
    const updatedTemplate = await updateTemplate(props.template.id, {
      title: props.template.title ?? data.title,
      templateHtml: data.templateHtml,
      templateJson: data.templateJson,
      includeLetterhead: data.includeLetterhead,
    });
    props.onClose();
    if (onSuccess) onSuccess(updatedTemplate);
  };

  const documentUpsert = async (data: TemplateFormData) => {
    try {
      if (props.template?.id) {
        // Update existing template
        await handleUpdateTemplate(data);
        showSnackbar({
          message: t('documentUpdated', { ns: 'documents' }),
          severity: 'success',
          horizontal: 'right',
          vertical: 'bottom',
        });
      } else {
        // Create new template
        await handleCreateTemplate(data);
        showSnackbar({
          message: t('documentCreated', { ns: 'documents' }),
          severity: 'success',
          horizontal: 'right',
          vertical: 'bottom',
        });
      }
    } catch (error: any) {
      showSnackbar({
        message:
          error?.message ||
          t(
            props.template?.id
              ? 'errorUpdatingDocument'
              : 'errorCreatingDocument',
            { ns: 'documents' }
          ),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    }
  };
  // const { openModal } = useModal(); // Removed unused import

  return (
    <SimpleDrawDlg
      {...props}
      open={props.open}
      handleClose={props.onClose}
      maxWidth="xl"
    >
      <WBTypography variant="h3" fontWeight={'bold'}>
        {t(
          taxCreate
            ? 'Preview Document'
            : props?.template?.id
            ? 'updateDocument'
            : 'createDocument',
          {
            ns: 'documents',
          }
        )}
      </WBTypography>
      <DocumentCreateForm
        taxCreate={taxCreate}
        taskCreationContext={taskCreationContext}
        loading={creating || updating}
        onSubmit={async (data) => {
          if (taxCreate && props.template) {
            // When editing from SendTemplateOption, only update the task template content
            // Don't update the database record
            onSuccess?.({
              ...props.template,
              templateHtml: data.templateHtml,
              templateJson: data.templateJson,
              includeLetterhead: data.includeLetterhead,
            });
            props.onClose();
            return;
          }
          documentUpsert(data);
        }}
        defaultTemplate={props.template}
      />
    </SimpleDrawDlg>
  );
}

export default DocumentCreateModal;
